// =============================================================================
// features/conversations/components/SyncHistoryAlertDialog.tsx — Pop-up "seu
// histórico não veio" (F50-S02).
//
// O pedido do Rogério: "se der erro na sincronização, faz aparecer uma
// mensagem na tela dela num pop-up que força ela ir pra tela de pareamento
// pra fazer isso". O gatilho e o texto vivem em `useSyncHistoryAlert`
// (hooks/) — este componente só apresenta.
//
// Padrão de portal + foco preso reaproveitado de
// `features/documents/components/DocumentUploadDialog.tsx` e
// `features/sales/components/useFocusTrap.ts` — não existe modal canônico
// neste repo (62 reimplementações de `role="dialog""), então a reprodução do
// padrão já usado é o caminho combinado com o time.
//
// Acessibilidade: `role="alertdialog"` (é um alerta que exige atenção, não um
// diálogo neutro), foco move para o botão primário ao abrir, Tab/Shift+Tab
// ciclam dentro do card, Escape fecha. Área de toque dos botões >= 40px de
// altura — a operação é por celular.
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/cn';
import { useFocusTrap } from '../../sales/components/useFocusTrap';

export interface SyncHistoryAlertDialogProps {
  open: boolean;
  onDismiss: () => void;
}

export function SyncHistoryAlertDialog({
  open,
  onDismiss,
}: SyncHistoryAlertDialogProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const containerRef = React.useRef<HTMLDivElement>(null);

  useFocusTrap({ containerRef, active: open, onEscape: onDismiss });

  if (!open) return null;

  const goToPairing = (): void => {
    onDismiss();
    navigate('/meu-whatsapp');
  };

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/30 backdrop-blur-[2px]"
        style={{ animation: 'fade-in 200ms ease both' }}
      />
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="sync-history-alert-title"
        aria-describedby="sync-history-alert-description"
        className="fixed left-1/2 top-1/2 z-[160] w-[calc(100%-2rem)] max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-md flex flex-col"
        style={{
          background: 'var(--bg-elev-1)',
          border: '1px solid var(--warning)',
          boxShadow: 'var(--elev-5)',
        }}
      >
        <div className="flex flex-col gap-3 px-6 py-6">
          <span
            aria-hidden="true"
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 9v4M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          </span>

          <h2
            id="sync-history-alert-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Suas conversas antigas ainda não apareceram
          </h2>

          <p id="sync-history-alert-description" className="font-sans text-sm text-ink-2">
            Seu WhatsApp está conectado, mas as conversas anteriores ainda não foram trazidas para o
            sistema. Abra a tela de conexão para tentar de novo.
          </p>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              'flex-1 min-h-[44px] font-sans text-sm font-medium px-4 rounded-sm',
              'border border-border-strong text-ink-2',
              'transition-colors duration-fast hover:bg-surface-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
            )}
          >
            Agora não
          </button>
          <button
            type="button"
            onClick={goToPairing}
            autoFocus
            className={cn(
              'flex-1 min-h-[44px] font-sans text-sm font-semibold px-4 rounded-sm',
              'bg-azul text-white',
              'transition-[filter] duration-fast hover:brightness-110',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
            )}
          >
            Abrir tela de conexão
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
